import { Paper, SimpleGrid, Stack, Text } from '@mantine/core';
import {
    createPayoutCascadesUrl,
    payoutOrderCascadeEventTypes,
    payoutOrderCascadeGroupSkipReasons,
} from '~/entities/payout';
import { KeyValuePair } from '~/shared/ui/KeyValuePair';
import { createOrderPriorityBadge } from '../../OrderDetails';
import { type CascadeGroup } from '../types';
import { getCascadeFilterValue, getEntityLabel, getGroupKey } from '../utils';
import styles from '../CascadeEvents.module.css';
import { CascadePathLink } from './CascadePathLink';
import { TextList } from './TextList';
import { TraderLinks } from './TraderLinks';

interface CascadeGroupCardProps {
    group: CascadeGroup;
    groupIndex: number;
}

export const CascadeGroupCard = ({ group, groupIndex }: CascadeGroupCardProps) => {
    const hasSummaryFields = Boolean(group.type || group.requested?.priority);
    const groupLabel = getEntityLabel(group.groupName?.value, group.groupId, `Группа ${groupIndex + 1}`);
    const groupFilterValue = getCascadeFilterValue(group.groupName?.value, group.groupId);

    return (
        <Paper
            key={getGroupKey(group)}
            withBorder
            radius='sm'
            p='sm'
            className={styles.groupCard}
        >
            <Stack gap='sm'>
                <Stack gap={2}>
                    <Text
                        size='xs'
                        c='dimmed'
                        fw={500}
                    >
                        Группа {groupIndex + 1}
                    </Text>

                    {groupFilterValue ? (
                        <CascadePathLink
                            label={groupLabel}
                            href={createPayoutCascadesUrl('groups', { name: groupFilterValue })}
                        />
                    ) : (
                        <Text
                            size='sm'
                            fw={500}
                        >
                            {groupLabel}
                        </Text>
                    )}
                </Stack>

                {hasSummaryFields && (
                    <SimpleGrid
                        cols={{ base: 1, sm: 2 }}
                        spacing='sm'
                        verticalSpacing='xs'
                    >
                        {group.type && (
                            <KeyValuePair
                                label='Статус'
                                value={payoutOrderCascadeEventTypes[group.type]}
                            />
                        )}

                        {group.requested?.priority && (
                            <KeyValuePair
                                label='Приоритет'
                                value={createOrderPriorityBadge(group.requested.priority)}
                            />
                        )}
                    </SimpleGrid>
                )}

                {group.requested && (
                    <KeyValuePair
                        label='Трейдеры'
                        value={<TraderLinks traders={group.requested.traders} />}
                    />
                )}

                {group.skipped && (
                    <Stack gap='sm'>
                        <KeyValuePair
                            label='Проверенные трейдеры'
                            value={<TraderLinks traders={group.skipped.checkedTraders} />}
                        />

                        <KeyValuePair
                            label='Причины'
                            value={
                                <TextList
                                    items={group.skipped.reasons?.map((reason) => {
                                        return payoutOrderCascadeGroupSkipReasons[reason];
                                    })}
                                />
                            }
                        />
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
};
